import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { getStockItemComponents } from '../../clients/multi_component_client';
import { logInfo } from '../../config_and_helpers/logger';
import { parentLotClosedByComponents } from '../../utils/stockItemOpenFields';
import StockItemTable from './StockItemTable';

const ComponentStockItemsTable = ({
  stockItem,
  product,
  onRefresh,
  useGlassTable = false,
  storageNodes = null,
  storagePlaceOptions = null,
}) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComponents = useCallback(async () => {
    if (!stockItem?.id) return null;

    setLoading(true);
    setError(null);

    try {
      // Get component stock items for this specific master stock item
      const response = await getStockItemComponents(stockItem.id);
      if (response.success) {
        const componentsData = response.data || [];
        setComponents(componentsData);
        setLoading(false);
        return componentsData; // Return the fresh data
      }
    } catch (error) {
      // Error handled silently
    }

    // Fallback: Show informational message
    setComponents([]);
    setError('No component stock items found for this master stock item.');
    setLoading(false);
    return []; // Return empty array as fallback
  }, [stockItem?.id]);

  // Function to handle component stock item updates and parent synchronization
  const handleComponentStockItemUpdate = useCallback(
    async updatedComponentData => {
      // First, refresh the component data
      const freshComponents = await fetchComponents();

      // Check if any component stock item was marked as in_use
      // If so, mark the parent master stock item as in_use too
      if (updatedComponentData?.data?.in_use && stockItem) {
        // Update the parent stock item's in_use status in the UI
        // This reflects the backend's automatic synchronization
        if (!stockItem.in_use) {
          // Update the parent stock item in the main table with the new status
          if (onRefresh) {
            // Update the parent stock item immediately since backend handles the relationship
            // Create a completely new object to ensure React detects the change
            const updatedParentStockItem = {
              ...stockItem,
              in_use: true,
              // Force a timestamp to ensure the object is different
              _updated: Date.now(),
            };
            onRefresh({
              data: updatedParentStockItem,
              replaceSingleItem: true,
            });
          }
        }
      }

      // Check if any component stock item was marked as NOT in_use
      // If so, check if ALL components are now not in use, and mark parent as not in use too
      if (updatedComponentData?.data?.in_use === false && stockItem) {
        // Use the fresh components data from fetchComponents()
        if (freshComponents) {
          // Check if all component stock items for this master stock item are not in use
          const allComponentsNotInUse = freshComponents.every(component => {
            const stockItems = component.stock_items || [];
            return stockItems.every(item => item.in_use === false);
          });

          if (allComponentsNotInUse && stockItem.in_use) {
            // The server turns the parent off and clears its open date and
            // opener. The child response does not include the parent, so apply
            // that clear on the row that is already on screen.
            if (onRefresh) {
              const updatedParentStockItem = {
                ...parentLotClosedByComponents(stockItem),
                _updated: Date.now(),
              };
              logInfo('ComponentStockItemsTable', 'parent lot left in use', {
                stockItemId: stockItem.id ?? null,
                inUse: false,
                openedOn: null,
                openedBy: null,
              });
              onRefresh({
                data: updatedParentStockItem,
                replaceSingleItem: true,
              });
            }
          }
        }
      }
    },
    [stockItem, onRefresh, fetchComponents]
  );

  useEffect(() => {
    fetchComponents();
  }, [fetchComponents]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Loading component stock items...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="info" className="mb-0">
        <Alert.Heading>Component Stock Items</Alert.Heading>
        <p className="mb-2">
          Component stock items are managed automatically by the system. When
          you create or update stock for this multi-component product, the
          corresponding component stock items are created automatically.
        </p>
        <hr />
        <p className="mb-0">
          <strong>How it works:</strong>
        </p>
        <ul className="mb-0">
          <li>
            Component stock items are created automatically when main product
            stock items are created
          </li>
          <li>
            The quantity matches the component's "quantity_per_component"
            setting
          </li>
          <li>
            Component stock items are managed automatically when main stock is
            updated
          </li>
        </ul>
        <p className="mt-2 mb-0">
          <small className="text-muted">
            To view individual component stock items, check the inventory of
            each component product.
          </small>
        </p>
      </Alert>
    );
  }

  if (components.length === 0) {
    return (
      <Alert variant="secondary" className="mb-0">
        <i className="fa fa-info-circle me-2"></i>
        No component stock items found for this stock item.
      </Alert>
    );
  }

  return (
    <div>
      {components.map(component => {
        const stockItems = component.stock_items || [];
        const requiredQuantity = component.required_quantity || 0;

        // Create a mock product object for the component
        const componentProduct = {
          id: component.component_id,
          name: component.component_name,
          cat_num: component.component_cat_num,
          location: component.component_location,
          stock: component.component_stock,
          supports_multi_components: false, // Components themselves don't support multi-components
        };

        // Calculate availability status
        const availableStockItems = stockItems.filter(
          item =>
            item.in_use === false &&
            (item.expiry === null || new Date(item.expiry) > new Date())
        );

        return (
          <div key={component.component_id} className="mb-4">
            <div
              className={
                useGlassTable
                  ? 'd-flex justify-content-center align-items-center mb-2 p-2 stock-items-table__component-header'
                  : 'd-flex justify-content-center align-items-center mb-2 p-2 bg-light rounded'
              }
            >
              <div className="text-center">
                <h6 className="mb-1">
                  <strong>{component.component_name}</strong>
                  <small className="text-muted ms-2">
                    ({component.component_cat_num})
                  </small>
                </h6>
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <small className="text-muted">
                    <strong>Quantity per master unit:</strong>{' '}
                    {requiredQuantity}
                  </small>
                  <small className="text-muted">
                    <strong>Available:</strong> {availableStockItems.length}{' '}
                    stock items
                  </small>
                  <small className="text-muted">
                    <strong>Total Stock:</strong> {component.component_stock}
                  </small>
                </div>
                <div className="mt-1">
                  <small className="text-muted">
                    Location: {component.component_location || 'N/A'}
                  </small>
                </div>
              </div>
            </div>

            {stockItems.length > 0 ? (
              <StockItemTable
                product={componentProduct}
                items={stockItems}
                updateStockItems={handleComponentStockItemUpdate} // Handle component updates and parent sync
                showBar={false}
                showHeader={false} // Don't show "Stock Items" header for sub components
                setTotalItemStock={() => {}} // No-op for components
                updateProductLogs={() => {}} // No-op for components
                updateProductStock={() => {}} // No-op for components
                addNewItem={false} // Don't allow adding new items to components
                addStockItem={undefined} // Don't allow adding new items to components
                useGlassTable={useGlassTable}
                storageNodes={storageNodes}
                storagePlaceOptions={storagePlaceOptions}
              />
            ) : (
              <div
                className={
                  useGlassTable
                    ? 'text-center p-3 border rounded stock-items-table__empty-panel'
                    : 'text-center p-3 border rounded'
                }
              >
                <small className="text-muted">
                  No stock items found for this component
                </small>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ComponentStockItemsTable;
